/**
 * src/app/(public)/estado/page.js
 *
 * Página pública estilo statuspage.io. Muestra el estado de cada
 * componente del SaaS y se auto-refresca cada 30s.
 *
 * Pública (sin login). El endpoint /api/health/components ya cachea
 * 30s en CDN, así que cargas masivas no martillan los servicios externos.
 *
 * Diseño minimalista, hereda paleta del lobby.
 */

'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  CheckCircle,
  Warning,
  XCircle,
  ArrowsClockwise,
  Pulse,
  Database,
  Lightning,
  ShieldCheck,
  CreditCard,
  Browser,
} from '@phosphor-icons/react/dist/ssr';

const REFRESH_MS = 30_000;

const STATUS_LABELS = {
  operational: 'Operacional',
  degraded: 'Rendimiento degradado',
  partial_outage: 'Interrupción parcial',
  major_outage: 'Interrupción mayor',
  down: 'Caído',
  unknown: 'Sin datos',
};

const STATUS_COLORS = {
  operational: '#22c55e',
  degraded: '#f59e0b',
  partial_outage: '#f59e0b',
  major_outage: '#ef4444',
  down: '#ef4444',
  unknown: '#71717a',
};

const COMPONENT_ICONS = {
  app: Browser,
  database: Database,
  auth: ShieldCheck,
  ai_provider: Lightning,
  payments: CreditCard,
};

function StatusIcon({ status, size = 16 }) {
  if (status === 'operational') return <CheckCircle size={size} weight="fill" color={STATUS_COLORS.operational} />;
  if (status === 'down') return <XCircle size={size} weight="fill" color={STATUS_COLORS.down} />;
  return <Warning size={size} weight="fill" color={STATUS_COLORS[status] || STATUS_COLORS.unknown} />;
}

const SEVERITY_COLORS = {
  minor: '#f59e0b',
  major: '#ef4444',
  critical: '#dc2626',
  maintenance: '#3b82f6',
};

const SEVERITY_LABELS = {
  minor: 'Menor',
  major: 'Importante',
  critical: 'Crítico',
  maintenance: 'Mantenimiento',
};

const STATUS_LABELS_INC = {
  investigating: 'Investigando',
  identified: 'Identificado',
  monitoring: 'Monitoreando',
  resolved: 'Resuelto',
  scheduled: 'Programado',
};

function IncidentRow({ incident }) {
  const [open, setOpen] = useState(false);
  const sevColor = SEVERITY_COLORS[incident.severity] || '#71717a';
  const isResolved = !!incident.resolved_at;
  const startDate = new Date(incident.started_at);
  const dateStr = startDate.toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = startDate.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <button onClick={() => setOpen(!open)} style={{
        background: 'transparent', border: 'none',
        padding: '14px 16px', width: '100%', textAlign: 'left',
        cursor: 'pointer', color: 'inherit', fontFamily: 'inherit',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{
              padding: '2px 8px', borderRadius: 4,
              background: `${sevColor}20`,
              color: sevColor, fontSize: '0.65rem',
              fontWeight: 700, textTransform: 'uppercase',
            }}>{SEVERITY_LABELS[incident.severity] || incident.severity}</span>
            <span style={{
              fontSize: '0.65rem',
              color: isResolved ? '#22c55e' : '#f59e0b',
              fontWeight: 700, textTransform: 'uppercase',
            }}>{isResolved ? 'Resuelto' : (STATUS_LABELS_INC[incident.status] || incident.status)}</span>
          </div>
          <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>
            {incident.title}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', marginTop: 2 }}>
            {dateStr} · {timeStr}
            {incident.source === 'automatic' && ' · auto-detectado'}
          </div>
        </div>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && incident.updates && incident.updates.length > 0 && (
        <div style={{
          padding: '4px 16px 14px',
          borderTop: '1px solid rgba(255,255,255,0.03)',
        }}>
          {incident.description && (
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', margin: '8px 0 12px', lineHeight: 1.6 }}>
              {incident.description}
            </p>
          )}
          <div style={{ borderLeft: '2px solid rgba(255,255,255,0.1)', paddingLeft: 14 }}>
            {incident.updates.slice().reverse().map((u, idx) => (
              <div key={idx} style={{ marginBottom: 12, position: 'relative' }}>
                <div style={{
                  position: 'absolute', left: -19, top: 4,
                  width: 8, height: 8, borderRadius: '50%',
                  background: idx === 0 ? sevColor : 'rgba(255,255,255,0.2)',
                }} />
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 700 }}>
                  {STATUS_LABELS_INC[u.status] || u.status} · {new Date(u.timestamp).toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', marginTop: 2 }}>
                  {u.message}
                </div>
              </div>
            ))}
          </div>
          {incident.postmortem_url && (
            <a href={incident.postmortem_url} target="_blank" rel="noreferrer" style={{
              display: 'inline-block', marginTop: 8,
              color: '#C5A059', fontSize: '0.78rem', fontWeight: 700,
              textDecoration: 'none',
            }}>
              Ver postmortem completo →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function EstadoPage() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [daysSinceLast, setDaysSinceLast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  async function fetchStatus() {
    try {
      const [statusRes, incidentsRes] = await Promise.all([
        fetch('/api/health/components', { cache: 'no-store' }),
        fetch('/api/incidents?days=30&limit=20', { cache: 'no-store' }),
      ]);
      const json = await statusRes.json();
      setData(json);
      setLastFetch(new Date());
      setError(null);
      if (incidentsRes.ok) {
        const incData = await incidentsRes.json();
        setIncidents(incData.incidents || []);
        setDaysSinceLast(incData.days_since_last_incident);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const overall = data?.status || 'unknown';
  const overallColor = STATUS_COLORS[overall];
  const overallLabel = STATUS_LABELS[overall];

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        {/* Header */}
        <header style={styles.header}>
          <Link href="/" style={styles.brandLink}>← Dr. Víctor Legal</Link>
          <h1 style={styles.title}>Estado del sistema</h1>
          <p style={styles.subtitle}>
            Estado en tiempo real de todos los componentes de Dr. Víctor.
            Auto-actualiza cada 30 segundos.
          </p>
        </header>

        {/* Big status banner */}
        <section style={{
          ...styles.bigBanner,
          background: `linear-gradient(135deg, ${overallColor}15 0%, ${overallColor}08 100%)`,
          borderColor: `${overallColor}55`,
        }}>
          <div style={styles.bannerLeft}>
            <StatusIcon status={overall} size={32} />
            <div>
              <h2 style={{ ...styles.bannerTitle, color: overallColor }}>
                {loading && !data ? 'Verificando…' : overallLabel}
              </h2>
              <p style={styles.bannerSubtitle}>
                {overall === 'operational'
                  ? 'Todos los sistemas operan con normalidad.'
                  : overall === 'degraded'
                  ? 'Algunos componentes tienen rendimiento reducido pero todo funciona.'
                  : overall === 'partial_outage'
                  ? 'Un servicio no responde. El resto de la plataforma sigue operativa.'
                  : overall === 'major_outage'
                  ? 'Múltiples servicios caídos. El equipo está investigando.'
                  : 'Cargando estado…'}
              </p>
            </div>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            style={styles.refreshBtn}
            title="Actualizar ahora"
          >
            <ArrowsClockwise size={14} weight="bold" />
            Actualizar
          </button>
        </section>

        {/* Components list */}
        <section style={styles.componentsSection}>
          <h3 style={styles.sectionTitle}>Componentes</h3>
          {data?.components?.length > 0 ? (
            <div style={styles.componentsList}>
              {data.components.map((c) => {
                const Icon = COMPONENT_ICONS[c.id] || Pulse;
                const compColor = STATUS_COLORS[c.status] || STATUS_COLORS.unknown;
                return (
                  <div key={c.id} style={styles.componentRow}>
                    <div style={styles.componentLeft}>
                      <Icon size={18} weight="duotone" color={compColor} />
                      <div>
                        <strong style={styles.componentName}>{c.name}</strong>
                        {c.latency_ms !== undefined && c.latency_ms > 0 && (
                          <div style={styles.componentMeta}>
                            Latencia: {c.latency_ms} ms
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={styles.componentRight}>
                      <StatusIcon status={c.status} size={16} />
                      <span style={{ color: compColor, fontSize: '0.78rem', fontWeight: 700 }}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : error ? (
            <p style={styles.errorText}>No pudimos consultar el estado: {error}</p>
          ) : (
            <p style={styles.loadingText}>Cargando componentes…</p>
          )}
        </section>

        {/* Incidentes recientes */}
        <section style={styles.componentsSection}>
          <h3 style={styles.sectionTitle}>
            Incidentes recientes
            {daysSinceLast !== null && daysSinceLast >= 7 && (
              <span style={{
                marginLeft: 12, padding: '2px 8px',
                background: 'rgba(34, 197, 94, 0.15)',
                color: '#22c55e',
                borderRadius: 999, fontSize: '0.65rem',
                fontWeight: 700, letterSpacing: 0,
                textTransform: 'none',
              }}>
                {daysSinceLast} días sin incidentes
              </span>
            )}
          </h3>
          {incidents.length === 0 ? (
            <div style={{
              padding: '1.5rem', textAlign: 'center',
              color: 'rgba(255,255,255,0.5)',
              background: 'rgba(34, 197, 94, 0.04)',
              border: '1px solid rgba(34, 197, 94, 0.15)',
              borderRadius: 12, fontSize: '0.85rem',
            }}>
              ✓ Sin incidentes en los últimos 30 días.
            </div>
          ) : (
            <div style={styles.componentsList}>
              {incidents.map((inc) => <IncidentRow key={inc.id} incident={inc} />)}
            </div>
          )}
        </section>

        {/* Trust footer */}
        <section style={styles.trustSection}>
          <h3 style={styles.sectionTitle}>Sobre esta página</h3>
          <p style={styles.trustParagraph}>
            Dr. Víctor monitorea continuamente todos los componentes críticos de la plataforma.
            Si detectas un problema que no aparece aquí, escríbenos a{' '}
            <a href="mailto:soporte@drvictorlegal.com" style={styles.linkAccent}>
              soporte@drvictorlegal.com
            </a>{' '}
            y nuestro equipo lo investiga.
          </p>
          <p style={styles.trustParagraph}>
            <strong>SLA objetivo:</strong> 99.5% uptime mensual. Reportes de incidentes pasados
            y postmortems se publican en este mismo lugar (próximamente).
          </p>
        </section>

        {/* Last update */}
        {lastFetch && (
          <div style={styles.lastUpdate}>
            Última verificación: {lastFetch.toLocaleTimeString('es-DO')}
            {' · '}
            <Link href="/" style={styles.linkAccent}>Volver al sitio</Link>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#000814',
    color: '#fff',
    padding: '2rem 1rem',
    fontFamily: 'Inter, sans-serif',
  },
  wrapper: {
    maxWidth: 760,
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
  },
  brandLink: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.85rem',
    textDecoration: 'none',
    marginBottom: '1rem',
    display: 'inline-block',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 300,
    letterSpacing: '-0.5px',
    margin: '8px 0',
    background: 'linear-gradient(to right, #fff, #C5A059)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    marginTop: 4,
  },
  bigBanner: {
    border: '1px solid',
    borderRadius: 16,
    padding: '1.5rem',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  bannerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  bannerTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    margin: 0,
  },
  bannerSubtitle: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '0.85rem',
    margin: '4px 0 0',
    maxWidth: 460,
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.85)',
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  componentsSection: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.85rem',
  },
  componentsList: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  componentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  componentLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  componentName: {
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  componentMeta: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '0.7rem',
    marginTop: 2,
  },
  componentRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  trustSection: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  trustParagraph: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.85rem',
    lineHeight: 1.6,
    marginBottom: '0.75rem',
  },
  linkAccent: {
    color: '#C5A059',
    textDecoration: 'none',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '0.85rem',
  },
  loadingText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.85rem',
  },
  lastUpdate: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '0.75rem',
    textAlign: 'center',
    marginTop: '2rem',
  },
};
